import { useState } from "react"
import { useCourses, useDeleteCourse, usePublishCourse, useArchiveCourse } from "@/features/courses/hooks/useCourses"
import { CourseCard } from "@/features/courses/components/CourseCard"
import { CourseFiltersBar } from "@/features/courses/components/CourseFiltersBar"
import { CreateCourseModal } from "@/features/courses/components/CreateCourseModal"
import { Button } from "@/components/ui/Button"
import { Alert } from "@/components/ui/Alert"
import type { CourseFilters, Course } from "@/features/courses/types"
import { useTranslation, Trans } from "react-i18next"

function CourseCardSkeleton() {
  return (
    <div className="animate-pulse rounded-xl border border-l-4 border-surface-200 border-l-neutral-200 bg-white p-5">
      <div className="mb-3 flex items-start justify-between">
        <div className="flex-1 space-y-2">
          <div className="h-4 w-3/4 rounded bg-surface-100" />
          <div className="h-3 w-1/2 rounded bg-surface-100" />
        </div>
        <div className="h-9 w-9 rounded-lg bg-surface-100" />
      </div>
      <div className="flex gap-2">
        <div className="h-5 w-12 rounded-full bg-surface-100" />
        <div className="h-5 w-14 rounded-full bg-surface-100" />
      </div>
    </div>
  )
}

export function CoursesPage() {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<CourseFilters>({ page: 1 })
  const [modalOpen, setModalOpen] = useState(false)
  const [editCourse, setEditCourse] = useState<Course | undefined>(undefined)
  const [deleteTarget, setDeleteTarget] = useState<Course | null>(null)

  const { data, isLoading, isError, refetch } = useCourses(filters)
  const deleteMutation = useDeleteCourse()
  const publishMutation = usePublishCourse()
  const archiveMutation = useArchiveCourse()

  const courses = data?.data ?? []
  const meta = data?.meta

  function openCreateModal() {
    setEditCourse(undefined)
    setModalOpen(true)
  }

  function openEditModal(course: Course) {
    setEditCourse(course)
    setModalOpen(true)
  }

  async function confirmDelete() {
    if (!deleteTarget) return
    await deleteMutation.mutateAsync(deleteTarget.id)
    setDeleteTarget(null)
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Page header */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">{t("courses.title")}</h1>
          {meta?.total !== undefined && (
            <p className="mt-0.5 text-sm text-neutral-600">
              {t("courses.total", { count: meta.total })}
            </p>
          )}
        </div>
        <Button onClick={openCreateModal}>{t("courses.new_course")}</Button>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <CourseFiltersBar
          filters={filters}
          onChange={(f) => setFilters({ ...f, page: 1 })}
        />
      </div>

      {/* Error state */}
      {isError && (
        <div className="mb-6">
          <Alert variant="error">
            {t("courses.load_error")}{" "}
            <button
              type="button"
              onClick={() => refetch()}
              className="underline hover:no-underline"
            >
              {t("common.retry")}
            </button>
          </Alert>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((n) => (
            <CourseCardSkeleton key={n} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && courses.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
          <svg
            width="72"
            height="72"
            viewBox="0 0 72 72"
            fill="none"
            aria-hidden="true"
            className="text-surface-200"
          >
            <rect x="10" y="10" width="52" height="52" rx="10" stroke="currentColor" strokeWidth="2.5" fill="none" />
            <path d="M24 28h24M24 36h16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
          <div>
            <p className="text-base font-semibold text-neutral-800">{t("courses.empty_title")}</p>
            <p className="mt-1 text-sm text-neutral-600">{t("courses.empty_hint")}</p>
          </div>
          <Button onClick={openCreateModal}>{t("courses.create")}</Button>
        </div>
      )}

      {/* Course grid */}
      {!isLoading && !isError && courses.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map((course) => (
            <CourseCard
              key={course.id}
              course={course}
              onEdit={openEditModal}
              onPublish={(id) => publishMutation.mutate(id)}
              onArchive={(id) => archiveMutation.mutate(id)}
              onDelete={(c) => setDeleteTarget(c)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {meta && meta.last_page && meta.last_page > 1 && (
        <div className="mt-8 flex items-center justify-between">
          <button
            type="button"
            disabled={(meta.current_page ?? 1) <= 1}
            onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
            className="rounded-lg border border-surface-200 bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-surface-50 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary"
            style={{ minHeight: "44px" }}
          >
            {t('common.previous')}
          </button>
          <span className="text-sm text-neutral-600">
            Page {meta.current_page ?? 1} sur {meta.last_page}
          </span>
          <button
            type="button"
            disabled={(meta.current_page ?? 1) >= (meta.last_page ?? 1)}
            onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
            className="rounded-lg border border-surface-200 bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-surface-50 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary"
            style={{ minHeight: "44px" }}
          >
            {t('common.next')}
          </button>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="alertdialog"
          aria-modal="true"
          aria-label={t("courses.delete_dialog_label")}
        >
          <div
            className="absolute inset-0 bg-neutral-900/50"
            onClick={() => setDeleteTarget(null)}
            aria-hidden="true"
          />
          <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-neutral-900">
              {t("courses.delete_title")}
            </h2>
            <p className="mt-2 text-sm text-neutral-600">
              <Trans
                i18nKey="courses.delete_warning"
                values={{ title: deleteTarget.title }}
                components={[<strong key="title" />]}
              />
            </p>
            <div className="mt-5 flex gap-3 justify-end">
              <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
                {t("common.cancel")}
              </Button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
                className="rounded-lg bg-mindeia-error px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-error focus-visible:ring-offset-2"
                style={{ minHeight: "44px" }}
              >
                {deleteMutation.isPending ? t("courses.deleting") : t("common.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit modal */}
      {modalOpen && (
        <CreateCourseModal
          open
          onClose={() => {
            setModalOpen(false)
            setEditCourse(undefined)
          }}
          editCourse={editCourse}
        />
      )}
    </div>
  )
}
